import { DesignVersionProvider } from 'mage-deps/@klarna/bubble-ui'
import { ConnectedRouter } from 'mage-deps/connected-react-router'
import PropTypes from 'prop-types'
import React, { useEffect, useMemo, useState } from 'react'
import Insights from 'mage-insights'
import { hooks as coreHooks } from 'mage-core'
import { useEducationFeature } from 'education-tools'

import UserMenuButton from '../Header/UserMenu'
import HelpfulQuestionMark from '../HelpfulQuestionMark'
import HelpContextProvider from '../HelpfulQuestionMark/HelpContextProvider'
import HelpViewport from '../HelpfulQuestionMark/Viewport'
import NotificationBanners from '../NotificationBanners'

import EnvironmentIdentifier from '../EnvironmentIdentifier'
import ApprlLogo from './assets/apprl-touch-icon-60x60.png'

const App = ({ App, mpApps, t, history, designVersion, location, footerConfig, clientId, onLogoutClick, Framework }) => {
  const apprlExperiment = useEducationFeature('mpux.third-party-apprl')
  const hasApprl = coreHooks.useTokenHelper().hasClientForEntityType('merchant', 'apprl')
  const [isApprlActive, setIsApprlActive] = useState(false)

  useEffect(() => {
    setIsApprlActive(apprlExperiment && hasApprl)
  }, [apprlExperiment, hasApprl])

  useEffect(() => {
    const title = t(`core.apps.${clientId}.title`)
    if (title) {
      document.title = `${title} | Klarna Merchant Portal`
    }
  }, [t, clientId])

  const sidebarApps = useMemo(() => {
    return mpApps.map(({ clientId, icon, isBottomItem, url, title, visibleInMenu, isExternalLink, isStaticPage }) => (visibleInMenu && {
      clientId,
      icon,
      id: `sidebar-${clientId}`,
      isBottomItem,
      isExternalLink,
      path: url,
      title: t(`core.apps.${clientId}.title`, {}, title),
      isStaticPage,
      onItemClick: () => Insights.trackSidebarNavigation({ label: clientId })
    }))
  }, [mpApps, t])

  const extraApps = useMemo(() => {
    return isApprlActive ? [{
      clientId: 'merchant-portal',
      icon: <img src={ApprlLogo} />,
      isExtraSection: true,
      path: '/third-party',
      title: 'APPRL',
      onItemClick: () => Insights.trackSidebarNavigation({ label: clientId })
    }] : []
  }, [t, isApprlActive])

  const sidebarConfig = useMemo(() => {
    return {
      extraSectionTitle: isApprlActive ? 'Apps' : '',
      items: [
        {
          path: '/',
          clientId: 'home-fe',
          title: t('core.apps.home-fe.title'),
          icon: 'IconHome',
          id: 'sidebar-home-fe',
          onItemClick: () => Insights.trackSidebarNavigation({ label: 'home-fe' })
        },
        ...sidebarApps,
        ...extraApps,
        {
          clientId: 'developers',
          icon: 'IconKeyboard',
          id: 'sidebar-developers',
          path: 'https://developers.klarna.com/?utm_source=Merchant%20Portal&utm_medium=Sidebar%20Menu&utm_campaign=external_referral',
          isExternalLink: true,
          title: t('core.apps.developers.title'),
          isBottomItem: true,
          onItemClick: () => Insights.trackSidebarNavigation({ label: 'developers' })
        }
      ]
    }
  }, [sidebarApps, t])

  const trackUserProfileToggle = (toggled) => Insights.event({
    category: 'topbar-actions',
    action: toggled ? 'close' : 'open',
    label: 'user-profile'
  })

  return (
    <ConnectedRouter history={history}>
      <HelpContextProvider>
        <EnvironmentIdentifier />
        <Framework
          additionalContent={(
            <>
              <HelpViewport />
            </>
          )}
          sidebarConfig={sidebarConfig}
          footerConfig={footerConfig}
          topbarActionButtons={{
            buttons: [
              <HelpfulQuestionMark key='uhqm-button' />,
              <UserMenuButton key='user-button' onToggle={trackUserProfileToggle} onLogoutClick={onLogoutClick} />
            ]
          }}
        >
          <DesignVersionProvider value={designVersion}>
            <NotificationBanners />
            <App location={location} />
          </DesignVersionProvider>
        </Framework>
      </HelpContextProvider>
    </ConnectedRouter>
  )
}

App.propTypes = {
  App: PropTypes.any,
  history: PropTypes.object,
  location: PropTypes.object,
  designVersion: PropTypes.string,
  mpApps: PropTypes.array.isRequired,
  t: PropTypes.func.isRequired,
  footerConfig: PropTypes.object.isRequired,
  clientId: PropTypes.string,
  Framework: PropTypes.any.isRequired,
  onLogoutClick: PropTypes.func.isRequired
}

export default App
